
// Explicit standalone React import at the very top
import * as React from 'react';
// Then import React DOM
import { createRoot } from 'react-dom/client';
// Now import React-based dependencies
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { AuthProvider } from './contexts/AuthContext';

// Import styles
import './styles/index.css';

// Import App component explicitly after React is imported
import App from './App';

// Import utility functions needed
import { rateLimiter } from './utils/rateLimiter';
import { videoQueue } from './utils/videoQueue';
import { isLowEndDevice, isMobileDevice } from './utils/environment';

// Error handler for production
const handleGlobalError = (event) => {
  console.error("Caught global error:", event.error);
  if (event.error && event.error.message && 
      event.error.message.includes("Cannot access 'R' before initialization")) {
    console.error("React initialization error detected - please refresh the page");
    // Optionally show a user-friendly message
    document.getElementById("root")?.setAttribute("data-error", "loading-error");
  }
};

// Set up error handler early
if (typeof window !== 'undefined') {
  window.addEventListener('error', handleGlobalError);
}

// Create query client before DOM initialization
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 10, // 10 minutes
      gcTime: 1000 * 60 * 20,    // 20 minutes
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (error instanceof Error && error.message.includes('Rate limit exceeded')) {
          return false;
        }
        // Implement exponential backoff for retries
        if (failureCount < 3) {
          const delay = rateLimiter.getBackoffDelay(failureCount);
          setTimeout(() => {}, delay);
          return true;
        }
        return false;
      },
      refetchOnMount: true,
      refetchOnReconnect: "always",
    },
  },
});

// Add type declaration for the global rate limiter
declare global {
  interface Window {
    __APP_RATE_LIMITER__?: {
      limit: (endpoint: string, fn: () => Promise<any>, options?: any) => Promise<any>;
      checkRateLimit: (endpoint: string) => boolean;
      getBackoffDelay: (attempt: number) => number;
    };
    __LOVABLE_DISABLE_BADGE__?: boolean;
  }
}

// Initialize video queue with configuration based on device
const initializeVideoQueue = () => {
  if (typeof window !== 'undefined') {
    const isMobile = isMobileDevice();
    const isLowEnd = isLowEndDevice();
    
    // Configure video queue based on device capabilities
    videoQueue.configure({
      maxConcurrentLoads: isLowEnd ? 1 : isMobile ? 2 : 3,
      stallTimeout: isLowEnd ? 10000 : 15000,
      reserveForViewport: 1
    });
    
    console.log("Video queue configured for", 
      isLowEnd ? "low-end device" : isMobile ? "mobile device" : "desktop device");
  }
};

// Setup global utilities
const setupGlobals = () => {
  if (typeof window !== 'undefined') {
    // Add rate limiter to window
    window.__APP_RATE_LIMITER__ = {
      limit: (endpoint: string, fn: () => Promise<any>, options = {}) => 
        rateLimiter.limit(endpoint, fn, options),
      checkRateLimit: (endpoint: string) => rateLimiter.checkRateLimit(endpoint),
      getBackoffDelay: (attempt: number) => rateLimiter.getBackoffDelay(attempt)
    };
    
    // Disable Lovable badge
    window.__LOVABLE_DISABLE_BADGE__ = true;
    
    // High-priority routes for immediate preloading
    const highPriorityRoutes = ['/auth/login', '/auth/signup', '/', '/playlists'];
    
    // Medium-priority routes for standard preloading
    const mediumPriorityRoutes = [
      '/clips', '/creators', '/derivatives',
      '/dashboard'
    ];
    
    // Utility to create preload links
    const createPreloadLink = (route: string, priority: 'high' | 'medium' = 'medium') => {
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.as = 'document';
      link.href = route;
      link.setAttribute('importance', priority);
      document.head.appendChild(link);
    };
    
    // Preload high priority routes immediately
    highPriorityRoutes.forEach(route => createPreloadLink(route, 'high'));
    
    // Preload medium priority routes during idle time
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => {
        mediumPriorityRoutes.forEach(route => createPreloadLink(route));
      });
    } else {
      // Fallback for browsers without requestIdleCallback
      setTimeout(() => {
        mediumPriorityRoutes.forEach(route => createPreloadLink(route));
      }, 1000);
    }
    
    // Optimized DNS prefetching and preconnection
    ['https://hudpwbqdvkfdqaypymwz.supabase.co', 'https://cdn.jsdelivr.net'].forEach(url => {
      // DNS prefetch
      const dnsPrefetch = document.createElement('link');
      dnsPrefetch.rel = 'dns-prefetch';
      dnsPrefetch.href = url;
      document.head.appendChild(dnsPrefetch);
      
      // Preconnect
      const preconnect = document.createElement('link');
      preconnect.rel = 'preconnect';
      preconnect.href = url;
      document.head.appendChild(preconnect);
    });
  }
};

// Add production error detection
const setupErrorDetection = () => {
  if (typeof window !== 'undefined') {
    // Create a minimal error display system
    const rootElement = document.getElementById("root");
    if (rootElement) {
      rootElement.style.position = 'relative';
      
      const errorContainer = document.createElement('div');
      errorContainer.id = 'react-error-container';
      errorContainer.style.display = 'none';
      errorContainer.style.position = 'fixed';
      errorContainer.style.top = '0';
      errorContainer.style.left = '0';
      errorContainer.style.right = '0';
      errorContainer.style.padding = '16px';
      errorContainer.style.backgroundColor = '#fee2e2';
      errorContainer.style.color = '#991b1b';
      errorContainer.style.zIndex = '9999';
      errorContainer.style.fontFamily = 'system-ui, sans-serif';
      errorContainer.style.textAlign = 'center';
      errorContainer.innerHTML = `
        <div>
          <strong>Erreur de chargement.</strong>
          <span>Veuillez rafraîchir la page ou vider le cache du navigateur.</span>
          <button id="reload-app" style="margin-left:8px;padding:4px 8px;background:#b91c1c;color:white;border:none;border-radius:4px;cursor:pointer;">
            Rafraîchir
          </button>
        </div>
      `;
      
      document.body.appendChild(errorContainer);
      
      // Add click handler for reload button
      document.getElementById('reload-app')?.addEventListener('click', () => {
        window.location.reload();
      });
      
      // Show error message if data-error attribute is set
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'data-error') {
            const hasError = rootElement.getAttribute('data-error') === 'loading-error';
            if (hasError) {
              errorContainer.style.display = 'block';
            }
          }
        });
      });
      
      observer.observe(rootElement, { attributes: true });
    }
  }
};

// First initialize video queue
initializeVideoQueue();

// Then setup global utilities
setupGlobals();

// Setup error detection
setupErrorDetection();

// Create root and render app - Ensure React is initialized by this point
// We don't use StrictMode in production to avoid double effect calls
const rootElement = document.getElementById("root");
if (rootElement) {
  try {
    createRoot(rootElement).render(
      <BrowserRouter>
        <HelmetProvider>
          <QueryClientProvider client={queryClient}>
            <LanguageProvider>
              <ThemeProvider>
                <AuthProvider>
                  <App />
                </AuthProvider>
              </ThemeProvider>
            </LanguageProvider>
          </QueryClientProvider>
        </HelmetProvider>
      </BrowserRouter>
    );
    
    console.log("App rendered successfully");
  } catch (error) {
    console.error("Error rendering application:", error);
    rootElement.setAttribute('data-error', 'loading-error');
  }
}
